import React from "react";
import { FormSubmitHandler } from "../../../common/models";
import { useCustomTranslation } from "../../../helpers/translateHelpers";
import {
  passwordComplexityValidation,
  samePasswordCheck,
  useValidators,
} from "../../../helpers/validation";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { handleFormServerErrors } from "../../../helpers/errorHandling";
import TextInput from "../TextInput";
import FormError from "../FormError";
import SubmitButton from "../SubmitButton";

interface Props {
  onSubmit: FormSubmitHandler<NewPasswordFormValues>;
}

export interface NewPasswordFormValues {
  password: string;
  passwordAgain: string;
}

const NewPasswordForm: React.FC<Props> = (props): React.ReactElement => {
  const { tInput, tError } = useCustomTranslation();
  const validators = useValidators();

  const methods = useForm<NewPasswordFormValues>({
    defaultValues: {
      password: "",
      passwordAgain: "",
    },
    mode: "onBlur",
  });

  const onSubmit: SubmitHandler<NewPasswordFormValues> = async (data) => {
    try {
      await props.onSubmit(data);
    } catch (e) {
      handleFormServerErrors(e, tError, methods.setError);
    }
  };

  const { handleSubmit } = methods;

  return (
    <FormProvider {...methods}>
      <form className="sm:w-[480px]" onSubmit={handleSubmit(onSubmit)}>
        <TextInput
          name="password"
          placeholder={tInput("password_input")}
          type="password"
          required
          options={{
            required: validators.required,
            minLength: validators.minLength(8),
            validate: {
              passwordComplexityValidation,
            },
          }}
        />
        <TextInput
          name="passwordAgain"
          placeholder={tInput("password_again_input")}
          type="password"
          required
          options={{
            required: validators.required,
            minLength: validators.minLength(8),
            validate: {
              passwordComplexityValidation,
              samePasswordCheck: (value, values) =>
                samePasswordCheck(value as string, values.password as string),
            },
          }}
        />
        <p className="text-gray-dark mt-2 mb-2 text-[14px]/[24px] tracking-[-0.14px]">
          {tInput("password_complex_required")}
        </p>
        <FormError />
        <SubmitButton
          label={tInput("password_reset_complete_button")}
          icon="arrow-right"
        />
      </form>
    </FormProvider>
  );
};

export default NewPasswordForm;
